/**
 * ============================================================================
 * WBR AGENT LAUNCHER — the "Ask WBR Agent" button in the page header controls.
 * ============================================================================
 *
 * Sits in the header controls row beside the Live/Snapshot toggle (passed as
 * DataModeSelector's `actions`). Opens the docked WBR Agent panel
 * (WbrAgentChatWidget, app-level) via the shared `chatAgentOpen` store flag.
 *
 * Feature-gated on `Feature.CHAT_AGENT` (fail-closed, same as the panel): it
 * renders nothing unless the caller's GetPermissions profile confirms access.
 * The `gen-ai` icon is Cloudscape's agent glyph.
 */

import React from 'react';
import Button from '@amzn/awsui-components-core/button';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { chatAgentOpened } from 'src/features/dashboard/dashboardSlice';
import {
  useGetPermissionsQuery,
  selectFeatureAccessible,
} from 'src/features/api/permissionsApi';
import { CHAT_AGENT_FEATURE_ID } from 'src/types/chatAgentApi.types';

const LAUNCH_LABEL = 'Ask WBR Agent';

export const WbrAgentLauncher: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: permissions } = useGetPermissionsQuery();
  const accessible = selectFeatureAccessible(
    permissions,
    CHAT_AGENT_FEATURE_ID,
  );
  if (!accessible) return null;

  return (
    <>
      {/* Leading divider — lives here (not in PageTabs) so it appears only
          alongside the button, never dangling when chat access is absent. */}
      <span className="page-tabs__divider" aria-hidden="true" />
      <Button
        variant="primary"
        iconName="gen-ai"
        onClick={() => dispatch(chatAgentOpened())}
      >
        {LAUNCH_LABEL}
      </Button>
    </>
  );
};
